import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useCreateCollection, useUpdateCollection } from "@/hooks/collections";
import type { PromptCollection } from "@/lib/types";

const EMOJI_OPTIONS = ["📁", "⭐", "💡", "🎨", "📝", "🚀", "💻", "📚", "🔥", "✨", "🎯", "💬"];

const formSchema = z.object({
  name: z.string().min(1, "Name is required").max(50, "Name too long"),
  description: z.string().max(200, "Description too long").optional(),
  emoji: z.string().default("📁"),
  isPublic: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingCollection?: PromptCollection | null;
  onSuccess?: (collection: any) => void;
}

export function CreateCollectionDialog({
  open,
  onOpenChange,
  editingCollection,
  onSuccess,
}: CreateCollectionDialogProps) {
  const createCollection = useCreateCollection();
  const updateCollection = useUpdateCollection();
  const isEditing = !!editingCollection;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      emoji: "📁",
      isPublic: false,
    },
  });

  useEffect(() => {
    if (editingCollection) {
      form.reset({
        name: editingCollection.name,
        description: editingCollection.description || "",
        emoji: editingCollection.emoji,
        isPublic: editingCollection.isPublic,
      });
    } else {
      form.reset({
        name: "",
        description: "",
        emoji: "📁",
        isPublic: false,
      });
    }
  }, [editingCollection, open]);

  const onSubmit = async (values: FormValues) => {
    if (isEditing && editingCollection) {
      updateCollection.mutate(
        {
          id: editingCollection.id,
          name: values.name,
          description: values.description,
          emoji: values.emoji,
          isPublic: values.isPublic,
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            onSuccess?.(null);
          },
        }
      );
    } else {
      createCollection.mutate(
        {
          name: values.name,
          description: values.description,
          emoji: values.emoji,
          isPublic: values.isPublic,
        },
        {
          onSuccess: (collection) => {
            onOpenChange(false);
            onSuccess?.(collection);
          },
        }
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Collection" : "Create Collection"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Emoji Picker */}
            <FormField
              control={form.control}
              name="emoji"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Icon</FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap gap-2">
                      {EMOJI_OPTIONS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => field.onChange(emoji)}
                          className={`text-2xl p-2 rounded-lg border-2 transition-colors ${
                            field.value === emoji
                              ? "border-primary bg-primary/10"
                              : "border-transparent hover:bg-muted"
                          }`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="My Marketing Prompts" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="A brief description of this collection..."
                      className="resize-none"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Public Toggle */}
            <FormField
              control={form.control}
              name="isPublic"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Public Collection</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Allow others to view this collection
                    </p>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createCollection.isPending || updateCollection.isPending}
              >
                {isEditing ? "Save Changes" : "Create Collection"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
